import type { Session } from '../../../shell/bridge';
import type { ChoreItem } from './types';

/** What the row's cadence chip says: the rule in the words a user picked. */
export function recurChip(chore: ChoreItem): string {
  if (chore.cadence !== 'recurring') return '1x';
  const mode = chore.recurMode ?? 'fixed';
  if (mode === 'interval') return `↺ every ${chore.recurEveryDays ?? 1}d`;
  if (mode === 'completion') return `↺ ${chore.recurEveryDays ?? 1}d after done`;
  return `↺ ${chore.freq ?? 'weekly'}`;
}

/** Epoch ms -> the `YYYY-MM-DD` an <input type="date"> holds, in local time
 *  (the same wall clock the form stamps on the way out). */
export function toDateInput(ms: number | null): string {
  if (ms == null) return '';
  const d = new Date(ms);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** Epoch ms -> the `HH:MM` an <input type="time"> holds; local noon is the
 *  date-only sentinel, so it reads as "no time picked". */
export function toTimeInput(ms: number | null): string {
  if (ms == null) return '';
  const d = new Date(ms);
  if (d.getHours() === 12 && d.getMinutes() === 0) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getHours())}:${p(d.getMinutes())}`;
}

export function dueLabel(chore: ChoreItem): string {
  if (chore.dueAt == null) return 'No date';
  const at = new Date(chore.dueAt);
  // Local noon is the date-only sentinel the date picker has always sent
  // (`T12:00:00`), so a row without a picked time still reads as a date and
  // one with a time shows the clock next to it.
  const hasTime = at.getHours() !== 12 || at.getMinutes() !== 0;
  // Spelled month, never the locale's `12/11/26`: a numeric date reads as
  // either day-first or month-first depending on who is looking, and that
  // ambiguity is what makes a correct row look like a wrong one (founder
  // report 2026-10-08).
  const day = at.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  if (!hasTime) return day;
  return `${day} · ${at.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
}

  /** Who left the receipt, in the panel's own words (Me / Partner). */
export function noteByLabel(chore: ChoreItem, me: Session | null): string {
    if (!chore.doneNoteBy) return '';
    if (me && chore.doneNoteBy === me.memberId) return 'Me';
    return 'Partner';
  };

  /** Who the row is on — the words the Done row keeps and the toast reads
   *  back (card 4kQn1aaw0rf9). Mode-blind on purpose: a finished round is a
   *  record of who was on it, not of the rule that put them there. */
export function assigneeLabel(chore: ChoreItem, me: Session | null): string {
    if (chore.assigneeId == null) return 'Anyone';
    if (me && chore.assigneeId === me.memberId) return 'Me';
    return 'Partner';
  };

  /** What the live pill says (card UeOwZSHfdZCh): a claim chore reads
   *  "Claim" while nobody has taken it, and an alternate one points at who
   *  is NEXT — the pointer the last completion handed over. */
export function pillLabel(chore: ChoreItem, me: Session | null): string {
    const mode = chore.assignMode ?? 'fixed';
    if (mode === 'claim' && chore.assigneeId == null) return 'Claim';
    if (mode === 'alternate' && chore.assigneeId != null) return `Next: ${assigneeLabel(chore, me)}`;
    return assigneeLabel(chore, me);
  };

  /** What the pill tells a screen reader about the tap it offers. */
export function pillAria(chore: ChoreItem, me: Session | null, partnerPhrase: string): string {
    const mode = chore.assignMode ?? 'fixed';
    if (mode === 'claim') {
      if (chore.assigneeId == null) return `Claim ${chore.title}`;
      return chore.assigneeId === (me?.memberId ?? '')
        ? `Claimed by you — tap to let go of ${chore.title}`
        : `Claimed by ${partnerPhrase} — ${chore.title}`;
    }
    return `Assigned to ${pillLabel(chore, me)}. Tap to reassign.`;
  };

  /** What the celebration says (card UeOwZSHfdZCh): the two modes that move
   *  the assignment on completion must name the DOER, because the row they
   *  read back is already about the next round — "Claim" and "Next: …" are
   *  not what a toast should answer "who did it?" with. */
export function toastLabel(chore: ChoreItem | null, me: Session | null): string {
    if (!chore) return 'Task done';
    const mode = chore.assignMode ?? 'fixed';
    if (mode === 'fixed') return `Task done — ${assigneeLabel(chore, me)}`;
    const doer = chore.completedBy == null ? null
      : chore.completedBy === (me?.memberId ?? '') ? 'Me' : 'Partner';
    return `Task done — ${doer ?? assigneeLabel(chore, me)}`;
  };
